'use client';
import React, { useState } from 'react';
import { computeModel, fmtCrShort, fmtPct, Assumptions } from '@/lib/financial-model';
import { Scale, CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react';
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';

// 2 TPH base assumptions (scaled from 5 TPH)
const BASE_2TPH: Assumptions = {
  tphCapacity: 2,
  operatingHoursPerDay: 20,
  operatingDaysPerYear: 300,
  year1Utilisation: 0.75,
  year2PlusUtilisation: 0.95,
  sellingPrice: 8000,
  priceEscalation: 0.03,
  carbonCreditsBase: 0,
  rawMaterialCost: 2700, // slightly higher per tonne at smaller scale
  powerCost: 1200,
  labourCost: 500, // higher per tonne — fixed labour spread over less volume
  maintenanceCost: 320,
  packagingCost: 150,
  otherVariableCost: 130,
  landLease: 1500000,
  salaries: 2800000, // lower absolute but higher per tonne
  insuranceMisc: 600000,
  machineryCapex: 13000000,
  civilCapex: 4000000,
  electricalCapex: 2000000,
  contingencyCapex: 1900000,
  workingCapital: 4000000,
  equityAmount: 10000000,
  debtAmount: 15000000,
  interestRate: 0.11,
  loanTenure: 7,
  moratorium: 1,
  depreciationPeriod: 10,
  taxRate: 0.25,
  discountRate: 0.14,
  inflationFixedCosts: 0.04,
};

const BASE_5TPH: Assumptions = {
  tphCapacity: 5,
  operatingHoursPerDay: 20,
  operatingDaysPerYear: 300,
  year1Utilisation: 0.75,
  year2PlusUtilisation: 0.95,
  sellingPrice: 8000,
  priceEscalation: 0.03,
  carbonCreditsBase: 0,
  rawMaterialCost: 2500,
  powerCost: 1100,
  labourCost: 350,
  maintenanceCost: 280,
  packagingCost: 150,
  otherVariableCost: 120,
  landLease: 1800000,
  salaries: 4200000,
  insuranceMisc: 900000,
  machineryCapex: 32000000,
  civilCapex: 7500000,
  electricalCapex: 4500000,
  contingencyCapex: 4500000,
  workingCapital: 14000000,
  equityAmount: 25000000,
  debtAmount: 37500000,
  interestRate: 0.11,
  loanTenure: 7,
  moratorium: 1,
  depreciationPeriod: 10,
  taxRate: 0.25,
  discountRate: 0.14,
  inflationFixedCosts: 0.04,
};

interface PlantConfig {
  label: string;
  color: string;
  bg: string;
  border: string;
  assumptions: Assumptions;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      style={{
        background: 'var(--surface-2)',
        border: '1px solid var(--divider)',
        borderRadius: '4px',
        padding: '10px 14px',
      }}
    >
      <p
        style={{
          fontSize: '0.7rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBottom: 6,
        }}
      >
        {label}
      </p>
      {payload.map((p: any) => (
        <div key={p.name} className="flex items-center gap-2" style={{ marginBottom: 2 }}>
          <div
            style={{ width: 8, height: 8, borderRadius: 1, background: p.color, flexShrink: 0 }}
          />
          <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>{p.name}:</span>
          <span
            style={{
              fontSize: '0.65rem',
              fontWeight: 700,
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {typeof p.value === 'number'
              ? p.value > 1
                ? `₹${p.value.toFixed(2)} Cr`
                : `${(p.value * 100).toFixed(1)}%`
              : p.value}
          </span>
        </div>
      ))}
    </div>
  );
};

interface ComparisonRowProps {
  label: string;
  val2: string;
  val5: string;
  winner?: '2tph' | '5tph' | 'tie';
  note?: string;
}

function ComparisonRow({ label, val2, val5, winner, note }: ComparisonRowProps) {
  return (
    <tr style={{ borderBottom: '1px solid var(--divider)' }}>
      <td
        className="py-2.5 px-4"
        style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 500 }}
      >
        <div>{label}</div>
        {note && (
          <div style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)', marginTop: 1 }}>
            {note}
          </div>
        )}
      </td>
      <td className="py-2.5 px-4 text-center">
        <span
          style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            fontFamily: 'var(--font-mono)',
            color:
              winner === '2tph'
                ? 'var(--upside)'
                : winner === '5tph'
                  ? 'var(--text-secondary)'
                  : 'var(--text-primary)',
          }}
        >
          {val2}
        </span>
        {winner === '2tph' && (
          <span className="ml-1.5" style={{ fontSize: '0.55rem', color: 'var(--upside)' }}>
            ✓
          </span>
        )}
      </td>
      <td className="py-2.5 px-4 text-center">
        <span
          style={{
            fontSize: '0.75rem',
            fontWeight: 700,
            fontFamily: 'var(--font-mono)',
            color:
              winner === '5tph'
                ? 'var(--gold)'
                : winner === '2tph'
                  ? 'var(--text-secondary)'
                  : 'var(--text-primary)',
          }}
        >
          {val5}
        </span>
        {winner === '5tph' && (
          <span className="ml-1.5" style={{ fontSize: '0.55rem', color: 'var(--gold)' }}>
            ✓
          </span>
        )}
      </td>
    </tr>
  );
}

export default function PlantDecisionContent() {
  const [compareCapacityA, setCompareCapacityA] = useState<number>(2);
  const [compareCapacityB, setCompareCapacityB] = useState<number>(5);

  function getScaledAssumptions(targetTph: number): Assumptions {
    const base = BASE_5TPH;
    const scale = targetTph / 5;
    return {
      ...base,
      tphCapacity: targetTph,
      machineryCapex: Math.round(base.machineryCapex * scale),
      civilCapex: Math.round(base.civilCapex * scale),
      electricalCapex: Math.round(base.electricalCapex * scale),
      contingencyCapex: Math.round(base.contingencyCapex * scale),
      workingCapital: Math.round(base.workingCapital * scale),
      equityAmount: Math.round(base.equityAmount * scale),
      debtAmount: Math.round(base.debtAmount * scale),
    };
  }

  const m2 = computeModel(getScaledAssumptions(compareCapacityA));
  const m5 = computeModel(getScaledAssumptions(compareCapacityB));

  const yr2_2 = m2.years[2];
  const yr2_5 = m5.years[2];

  // Radar chart — normalized scores (0-100)
  function normalize(val: number, min: number, max: number) {
    return Math.max(0, Math.min(100, ((val - min) / (max - min)) * 100));
  }

  const radarData = [
    {
      metric: 'IRR',
      capA: normalize(m2.projectIRR, 0, 0.5),
      capB: normalize(m5.projectIRR, 0, 0.5),
    },
    {
      metric: 'NPV',
      capA: normalize(m2.projectNPV, 0, 200000000),
      capB: normalize(m5.projectNPV, 0, 200000000),
    },
    {
      metric: 'EBITDA%',
      capA: normalize(yr2_2?.ebitdaMarginPct ?? 0, 0, 0.5),
      capB: normalize(yr2_5?.ebitdaMarginPct ?? 0, 0, 0.5),
    },
    {
      metric: 'DSCR',
      capA: normalize(yr2_2?.dscr ?? 0, 0, 5),
      capB: normalize(yr2_5?.dscr ?? 0, 0, 5),
    },
    { metric: 'Scale', capA: Math.min(100, compareCapacityA * 20), capB: Math.min(100, compareCapacityB * 20) },
    { metric: 'Low CAPEX', capA: Math.max(0, 100 - compareCapacityA * 15), capB: Math.max(0, 100 - compareCapacityB * 15) },
  ];

  const barData = [
    {
      name: 'Yr1 Rev',
      capA: +(m2.years[1]?.revenue / 1e7).toFixed(2),
      capB: +(m5.years[1]?.revenue / 1e7).toFixed(2),
    },
    {
      name: 'Yr2 Rev',
      capA: +(m2.years[2]?.revenue / 1e7).toFixed(2),
      capB: +(m5.years[2]?.revenue / 1e7).toFixed(2),
    },
    {
      name: 'Yr2 EBITDA',
      capA: +(m2.years[2]?.ebitda / 1e7).toFixed(2),
      capB: +(m5.years[2]?.ebitda / 1e7).toFixed(2),
    },
    {
      name: 'Yr2 PAT',
      capA: +(m2.years[2]?.pat / 1e7).toFixed(2),
      capB: +(m5.years[2]?.pat / 1e7).toFixed(2),
    },
    {
      name: 'NPV',
      capA: +(m2.projectNPV / 1e7).toFixed(2),
      capB: +(m5.projectNPV / 1e7).toFixed(2),
    },
  ];

  const verdict5wins = m5.projectIRR > m2.projectIRR && m5.projectNPV > m2.projectNPV;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-1">
          <div className="gold-bar h-6" />
          <h1
            style={{
              fontSize: '1.375rem',
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
            }}
          >
            Plant Capacity Decision: {compareCapacityA} TPH vs {compareCapacityB} TPH
          </h1>
        </div>
        <p
          style={{
            fontSize: '0.75rem',
            color: 'var(--text-tertiary)',
            letterSpacing: '0.04em',
            paddingLeft: '1rem',
          }}
        >
          Investment decision framework — CAPEX · Returns · Scale economics · Risk profile ·
          Go/No-Go recommendation
        </p>
      </div>

      {/* Verdict banner */}
      <div
        className="rounded p-4 flex items-start gap-4"
        style={{
          background: verdict5wins ? 'rgba(201,168,76,0.06)' : 'rgba(74,158,255,0.06)',
          border: `1px solid ${verdict5wins ? 'rgba(201,168,76,0.25)' : 'rgba(74,158,255,0.25)'}`,
        }}
      >
        <div
          className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0"
          style={{ background: verdict5wins ? 'rgba(201,168,76,0.15)' : 'rgba(74,158,255,0.15)' }}
        >
          <Scale size={20} style={{ color: verdict5wins ? 'var(--gold)' : 'var(--accent)' }} />
        </div>
        <div>
          <p
            style={{
              fontSize: '0.7rem',
              fontWeight: 800,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: verdict5wins ? 'var(--gold)' : 'var(--accent)',
              marginBottom: 4,
            }}
          >
            RECOMMENDATION:{' '}
            {verdict5wins ? `${compareCapacityB} TPH PLANT — GO WITH CONDITIONS` : `${compareCapacityA} TPH PLANT — LOWER RISK ENTRY`}
          </p>
          <p style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            {verdict5wins
              ? `${compareCapacityB} TPH delivers superior IRR (${(m5.projectIRR * 100).toFixed(1)}% vs ${(m2.projectIRR * 100).toFixed(1)}%), higher NPV (${fmtCrShort(m5.projectNPV)} vs ${fmtCrShort(m2.projectNPV)}), and better fixed-cost absorption per tonne. Recommended subject to debt availability and feedstock security.`
              : `${compareCapacityA} TPH offers lower CAPEX entry point (${fmtCrShort(m2.totalProjectCost)}) with faster payback. Consider ${compareCapacityA} TPH as Phase 1 with expansion optionality to ${compareCapacityB} TPH in Year 3–4.`}
          </p>
        </div>
      </div>

      {/* Side-by-side plant cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* 2 TPH */}
        <div className="exec-panel" style={{ borderTop: '2px solid var(--accent)' }}>
          <div className="exec-panel-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={compareCapacityA}
                  onChange={(e) => setCompareCapacityA(Number(e.target.value))}
                  className="w-16 p-1 rounded outline-none text-center font-mono"
                  style={{
                    background: 'var(--surface-3)',
                    color: 'var(--accent)',
                    border: '1px solid var(--divider)',
                    fontSize: '1rem',
                    fontWeight: 800
                  }}
                  min={1}
                />
                <h2
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 800,
                    color: 'var(--accent)',
                    letterSpacing: '0.04em',
                  }}
                >
                  TPH PLANT
                </h2>
              </div>
              <span
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  background: 'rgba(74,158,255,0.1)',
                  color: 'var(--accent)',
                  border: '1px solid rgba(74,158,255,0.2)',
                  padding: '3px 8px',
                  borderRadius: '2px',
                }}
              >
                CONSERVATIVE
              </span>
            </div>
          </div>
          <div className="p-4 space-y-2.5">
            {[
              { label: 'Total CAPEX', value: fmtCrShort(m2.totalProjectCost) },
              { label: 'Equity Required', value: fmtCrShort(m2.assumptions.equityAmount) },
              { label: 'Debt Required', value: fmtCrShort(m2.assumptions.debtAmount) },
              {
                label: 'Design Capacity',
                value: `${m2.designCapacity.toLocaleString('en-IN')} T/yr`,
              },
              { label: 'Yr1 Revenue', value: fmtCrShort(m2.years[1]?.revenue ?? 0) },
              { label: 'Yr2 Revenue', value: fmtCrShort(m2.years[2]?.revenue ?? 0) },
              { label: 'Yr2 EBITDA', value: fmtCrShort(m2.years[2]?.ebitda ?? 0) },
              { label: 'Yr2 EBITDA Margin', value: fmtPct(m2.years[2]?.ebitdaMarginPct ?? 0) },
              { label: 'Yr2 PAT', value: fmtCrShort(m2.years[2]?.pat ?? 0) },
              {
                label: 'Project IRR',
                value: `${(m2.projectIRR * 100).toFixed(1)}%`,
                highlight: true,
              },
              { label: 'Equity IRR', value: `${(m2.equityIRR * 100).toFixed(1)}%` },
              { label: 'NPV @ 14%', value: fmtCrShort(m2.projectNPV), highlight: true },
              {
                label: 'Payback',
                value:
                  m2.paybackYearFractional > 0
                    ? `Yr ${m2.paybackYearFractional.toFixed(1)}`
                    : 'N/A',
              },
              { label: 'DSCR (Yr2)', value: `${(m2.years[2]?.dscr ?? 0).toFixed(2)}x` },
              {
                label: 'VC per Tonne',
                value: `₹${m2.totalVariableCostPerTonne.toLocaleString('en-IN')}/t`,
              },
            ].map((item) => (
              <div
                key={item.label}
                className="flex justify-between items-center py-1"
                style={{ borderBottom: '1px solid var(--divider)' }}
              >
                <span style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)' }}>
                  {item.label}
                </span>
                <span
                  style={{
                    fontSize: item.highlight ? '0.875rem' : '0.75rem',
                    fontWeight: item.highlight ? 800 : 700,
                    fontFamily: 'var(--font-mono)',
                    color: item.highlight ? 'var(--accent)' : 'var(--text-primary)',
                  }}
                >
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* 5 TPH */}
        <div className="exec-panel" style={{ borderTop: '2px solid var(--gold)' }}>
          <div className="exec-panel-header">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={compareCapacityB}
                  onChange={(e) => setCompareCapacityB(Number(e.target.value))}
                  className="w-16 p-1 rounded outline-none text-center font-mono"
                  style={{
                    background: 'var(--surface-3)',
                    color: 'var(--gold)',
                    border: '1px solid var(--divider)',
                    fontSize: '1rem',
                    fontWeight: 800
                  }}
                  min={1}
                />
                <h2
                  style={{
                    fontSize: '0.875rem',
                    fontWeight: 800,
                    color: 'var(--gold)',
                    letterSpacing: '0.04em',
                  }}
                >
                  TPH PLANT
                </h2>
              </div>
              <span
                style={{
                  fontSize: '0.6rem',
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  background: 'rgba(201,168,76,0.1)',
                  color: 'var(--gold)',
                  border: '1px solid rgba(201,168,76,0.2)',
                  padding: '3px 8px',
                  borderRadius: '2px',
                }}
              >
                RECOMMENDED
              </span>
            </div>
          </div>
          <div className="p-4 space-y-2.5">
            {[
              { label: 'Total CAPEX', value: fmtCrShort(m5.totalProjectCost) },
              { label: 'Equity Required', value: fmtCrShort(m5.assumptions.equityAmount) },
              { label: 'Debt Required', value: fmtCrShort(m5.assumptions.debtAmount) },
              {
                label: 'Design Capacity',
                value: `${m5.designCapacity.toLocaleString('en-IN')} T/yr`,
              },
              { label: 'Yr1 Revenue', value: fmtCrShort(m5.years[1]?.revenue ?? 0) },
              { label: 'Yr2 Revenue', value: fmtCrShort(m5.years[2]?.revenue ?? 0) },
              { label: 'Yr2 EBITDA', value: fmtCrShort(m5.years[2]?.ebitda ?? 0) },
              { label: 'Yr2 EBITDA Margin', value: fmtPct(m5.years[2]?.ebitdaMarginPct ?? 0) },
              { label: 'Yr2 PAT', value: fmtCrShort(m5.years[2]?.pat ?? 0) },
              {
                label: 'Project IRR',
                value: `${(m5.projectIRR * 100).toFixed(1)}%`,
                highlight: true,
              },
              { label: 'Equity IRR', value: `${(m5.equityIRR * 100).toFixed(1)}%` },
              { label: 'NPV @ 14%', value: fmtCrShort(m5.projectNPV), highlight: true },
              {
                label: 'Payback',
                value:
                  m5.paybackYearFractional > 0
                    ? `Yr ${m5.paybackYearFractional.toFixed(1)}`
                    : 'N/A',
              },
              { label: 'DSCR (Yr2)', value: `${(m5.years[2]?.dscr ?? 0).toFixed(2)}x` },
              {
                label: 'VC per Tonne',
                value: `₹${m5.totalVariableCostPerTonne.toLocaleString('en-IN')}/t`,
              },
            ].map((item) => (
              <div
                key={item.label}
                className="flex justify-between items-center py-1"
                style={{ borderBottom: '1px solid var(--divider)' }}
              >
                <span style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)' }}>
                  {item.label}
                </span>
                <span
                  style={{
                    fontSize: item.highlight ? '0.875rem' : '0.75rem',
                    fontWeight: item.highlight ? 800 : 700,
                    fontFamily: 'var(--font-mono)',
                    color: item.highlight ? 'var(--gold)' : 'var(--text-primary)',
                  }}
                >
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Head-to-head comparison table */}
      <div className="exec-panel">
        <div className="exec-panel-header">
          <h2
            style={{
              fontSize: '0.75rem',
              fontWeight: 700,
              color: 'var(--text-primary)',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}
          >
            Head-to-Head Decision Matrix
          </h2>
          <p style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
            ✓ marks the winner per metric
          </p>
        </div>
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full" style={{ minWidth: '500px' }}>
            <thead>
              <tr
                style={{ borderBottom: '1px solid var(--divider)', background: 'var(--surface-2)' }}
              >
                <th
                  className="text-left py-2.5 px-4"
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--text-tertiary)',
                    minWidth: 180,
                  }}
                >
                  Metric
                </th>
                <th
                  className="py-2.5 px-4 text-center"
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--accent)',
                    minWidth: 130,
                  }}
                >
                  {compareCapacityA} TPH
                </th>
                <th
                  className="py-2.5 px-4 text-center"
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: 'var(--gold)',
                    minWidth: 130,
                  }}
                >
                  {compareCapacityB} TPH
                </th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ background: 'var(--surface-2)' }}>
                <td
                  colSpan={3}
                  className="px-4 py-1.5"
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'var(--gold)',
                  }}
                >
                  CAPEX & Financing
                </td>
              </tr>
              <ComparisonRow
                label="Total Project Cost"
                val2={fmtCrShort(m2.totalProjectCost)}
                val5={fmtCrShort(m5.totalProjectCost)}
                winner="2tph"
                note="Lower entry barrier"
              />
              <ComparisonRow
                label="Equity Required"
                val2={fmtCrShort(m2.assumptions.equityAmount)}
                val5={fmtCrShort(m5.assumptions.equityAmount)}
                winner="2tph"
              />
              <ComparisonRow
                label="Debt Required"
                val2={fmtCrShort(m2.assumptions.debtAmount)}
                val5={fmtCrShort(m5.assumptions.debtAmount)}
                winner="2tph"
              />
              <ComparisonRow
                label="Design Capacity"
                val2={`${m2.designCapacity.toLocaleString('en-IN')} T`}
                val5={`${m5.designCapacity.toLocaleString('en-IN')} T`}
                winner="5tph"
                note="2.5× more output"
              />
              <tr style={{ background: 'var(--surface-2)' }}>
                <td
                  colSpan={3}
                  className="px-4 py-1.5"
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'var(--gold)',
                  }}
                >
                  Returns
                </td>
              </tr>
              <ComparisonRow
                label="Project IRR"
                val2={`${(m2.projectIRR * 100).toFixed(1)}%`}
                val5={`${(m5.projectIRR * 100).toFixed(1)}%`}
                winner={m5.projectIRR > m2.projectIRR ? '5tph' : '2tph'}
                note="Higher = better"
              />
              <ComparisonRow
                label="Equity IRR"
                val2={`${(m2.equityIRR * 100).toFixed(1)}%`}
                val5={`${(m5.equityIRR * 100).toFixed(1)}%`}
                winner={m5.equityIRR > m2.equityIRR ? '5tph' : '2tph'}
              />
              <ComparisonRow
                label="NPV @ 14%"
                val2={fmtCrShort(m2.projectNPV)}
                val5={fmtCrShort(m5.projectNPV)}
                winner={m5.projectNPV > m2.projectNPV ? '5tph' : '2tph'}
                note="Absolute value creation"
              />
              <ComparisonRow
                label="Payback Period"
                val2={
                  m2.paybackYearFractional > 0 ? `Yr ${m2.paybackYearFractional.toFixed(1)}` : 'N/A'
                }
                val5={
                  m5.paybackYearFractional > 0 ? `Yr ${m5.paybackYearFractional.toFixed(1)}` : 'N/A'
                }
                winner={m2.paybackYearFractional < m5.paybackYearFractional ? '2tph' : '5tph'}
                note="Lower = faster recovery"
              />
              <tr style={{ background: 'var(--surface-2)' }}>
                <td
                  colSpan={3}
                  className="px-4 py-1.5"
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'var(--gold)',
                  }}
                >
                  Profitability
                </td>
              </tr>
              <ComparisonRow
                label="Yr2 EBITDA"
                val2={fmtCrShort(m2.years[2]?.ebitda ?? 0)}
                val5={fmtCrShort(m5.years[2]?.ebitda ?? 0)}
                winner={m5.years[2]?.ebitda > m2.years[2]?.ebitda ? '5tph' : '2tph'}
              />
              <ComparisonRow
                label="Yr2 EBITDA Margin"
                val2={fmtPct(m2.years[2]?.ebitdaMarginPct ?? 0)}
                val5={fmtPct(m5.years[2]?.ebitdaMarginPct ?? 0)}
                winner={
                  m5.years[2]?.ebitdaMarginPct > m2.years[2]?.ebitdaMarginPct ? '5tph' : '2tph'
                }
                note="Scale advantage"
              />
              <ComparisonRow
                label="Yr2 PAT"
                val2={fmtCrShort(m2.years[2]?.pat ?? 0)}
                val5={fmtCrShort(m5.years[2]?.pat ?? 0)}
                winner={m5.years[2]?.pat > m2.years[2]?.pat ? '5tph' : '2tph'}
              />
              <ComparisonRow
                label="VC per Tonne"
                val2={`₹${m2.totalVariableCostPerTonne.toLocaleString('en-IN')}`}
                val5={`₹${m5.totalVariableCostPerTonne.toLocaleString('en-IN')}`}
                winner={
                  m5.totalVariableCostPerTonne < m2.totalVariableCostPerTonne ? '5tph' : '2tph'
                }
                note="Lower = better unit economics"
              />
              <tr style={{ background: 'var(--surface-2)' }}>
                <td
                  colSpan={3}
                  className="px-4 py-1.5"
                  style={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: 'var(--gold)',
                  }}
                >
                  Risk & Debt Service
                </td>
              </tr>
              <ComparisonRow
                label="DSCR (Year 2)"
                val2={`${(m2.years[2]?.dscr ?? 0).toFixed(2)}x`}
                val5={`${(m5.years[2]?.dscr ?? 0).toFixed(2)}x`}
                winner={m5.years[2]?.dscr > m2.years[2]?.dscr ? '5tph' : '2tph'}
                note="Target > 1.5x"
              />
              <ComparisonRow
                label="DSCR (Year 3)"
                val2={`${(m2.years[3]?.dscr ?? 0).toFixed(2)}x`}
                val5={`${(m5.years[3]?.dscr ?? 0).toFixed(2)}x`}
                winner={m5.years[3]?.dscr > m2.years[3]?.dscr ? '5tph' : '2tph'}
              />
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div className="exec-panel">
          <div className="exec-panel-header">
            <h3
              style={{
                fontSize: '0.7rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Financial Performance Comparison
            </h3>
            <p style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
              ₹ Crores
            </p>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={barData} barCategoryGap="30%" barGap={4}>
                <CartesianGrid strokeDasharray="2 4" stroke="var(--divider)" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fill: 'var(--text-tertiary)', fontSize: 9, fontWeight: 600 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: 'var(--text-tertiary)', fontSize: 9 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => `₹${v}Cr`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    paddingTop: 8,
                  }}
                  formatter={(v) => <span style={{ color: 'var(--text-secondary)' }}>{v}</span>}
                />
                <Bar dataKey="capA" name={`${compareCapacityA} TPH`} fill="var(--accent)" radius={[2, 2, 0, 0]} opacity={0.85} />
                <Bar dataKey="capB" name={`${compareCapacityB} TPH`} fill="var(--gold)" radius={[2, 2, 0, 0]} opacity={0.9} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="exec-panel">
          <div className="exec-panel-header">
            <h3
              style={{
                fontSize: '0.7rem',
                fontWeight: 700,
                color: 'var(--text-primary)',
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}
            >
              Multi-Dimensional Score Radar
            </h3>
            <p style={{ fontSize: '0.6rem', color: 'var(--text-tertiary)', marginTop: 2 }}>
              Normalized 0–100 across 6 dimensions
            </p>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={240}>
              <RadarChart data={radarData}>
                <PolarGrid stroke="var(--divider)" />
                <PolarAngleAxis
                  dataKey="metric"
                  tick={{ fill: 'var(--text-tertiary)', fontSize: 10, fontWeight: 600 }}
                />
                <Radar
                  name={`${compareCapacityA} TPH`}
                  dataKey="capA"
                  stroke="var(--accent)"
                  fill="var(--accent)"
                  fillOpacity={0.15}
                  strokeWidth={2}
                />
                <Radar
                  name={`${compareCapacityB} TPH`}
                  dataKey="capB"
                  stroke="var(--gold)"
                  fill="var(--gold)"
                  fillOpacity={0.15}
                  strokeWidth={2}
                />
                <Legend
                  wrapperStyle={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    paddingTop: 8,
                  }}
                  formatter={(v) => <span style={{ color: 'var(--text-secondary)' }}>{v}</span>}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Decision factors */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="exec-panel">
          <div className="exec-panel-header">
            <div className="flex items-center gap-2">
              <CheckCircle2 size={14} style={{ color: 'var(--upside)' }} />
              <h3
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: 'var(--upside)',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                Choose {compareCapacityB} TPH When
              </h3>
            </div>
          </div>
          <div className="p-4">
            <ul className="space-y-2">
              {[
                'Equity of ₹2.5 Cr+ is available or can be raised',
                'Debt of ₹3.75 Cr+ is accessible (CGTMSE / bank term loan)',
                'Feedstock supply of 28,500+ T/yr is secured or contracted',
                'Offtake agreement with NTPC / TPP / industrial buyer in place',
                'Long-term scale and market leadership is the objective',
                'Carbon credit monetisation is being pursued (adds ₹3–5 Cr/yr)',
              ].map((pt, i) => (
                <li key={`5tph-${i}`} className="flex items-start gap-2">
                  <ArrowRight
                    size={11}
                    style={{ color: 'var(--gold)', marginTop: 3, flexShrink: 0 }}
                  />
                  <span
                    style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}
                  >
                    {pt}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="exec-panel">
          <div className="exec-panel-header">
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} style={{ color: 'var(--accent)' }} />
              <h3
                style={{
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  color: 'var(--accent)',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                }}
              >
                Choose 2 TPH When
              </h3>
            </div>
          </div>
          <div className="p-4">
            <ul className="space-y-2">
              {[
                'Equity is constrained to ₹1 Cr range — lower capital risk',
                'Feedstock supply is uncertain — test market with smaller volume',
                'First-time operator — build operational experience at lower scale',
                'Regulatory / land approvals are still in progress',
                'Phase 1 strategy: prove model, then expand to 5 TPH in Yr 3–4',
                'Faster payback preferred over maximum NPV',
              ].map((pt, i) => (
                <li key={`2tph-${i}`} className="flex items-start gap-2">
                  <ArrowRight
                    size={11}
                    style={{ color: 'var(--accent)', marginTop: 3, flexShrink: 0 }}
                  />
                  <span
                    style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}
                  >
                    {pt}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
